import {AnalyticsTab} from './store';
import {computed, observable, toJS} from 'mobx';
import {
    AbtestSortAttribute,
    ChannelDataRegister,
    SortMode,
    TableDataForChannel,
    TableDataForChannelRequest,
    TableDataForChannelResponse,
} from '../types/api/tableData/TableDataForChannel';
import * as TableAPI from '../apis/TableAPI';
import {color, ColorHelper} from 'csx';
import {StickyRowSumsRequest} from '../types/api/tableData/SumRowData';
import {DescriptiveKPIAdditions, isKpiGroupKeyOrDescriptiveKpiAdditions, KPI, KPIGroupKey} from '../kpi';
import {
    CompareColumnInfo,
    DataKey,
    FilterRule,
    NoneKPIColumns,
    SelectorTableColumnDef,
    SelectorTableColumnDefs,
    TableFilter,
} from './GeneralSelectorTableStore';
import * as AbTestAPI from '../apis/AbTestAPI';
import _ from 'lodash';
import {DIAGRAM_COLORS} from '../utility/ColorUtils';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import {Channel} from '../types/Channel';
import {TableSortDirection} from '../types/Table';
import {DateUtils} from '../utility/DateUtils';
import {BlackColor} from '../styles/colors';
import {DateRange} from './TimeIntervalStore';
import moment from 'moment';

export interface VariantChannelCompare {
    testId: string;
    variantIds: string[];
}

export interface CompareData {
    compareEnabled: boolean;
    channelCompare?: {
        channelNames: string[];
    };
    variantChannelCompare?: VariantChannelCompare;
}

export interface RatioResultItem {
    channel: string;
    color: ColorHelper;
    percentage: number;
}

export enum CompareMode {
    AbsDiffMode = 'absDiffMode',
    RatioMode = 'ratioMode',
    ChannelOrVariantColumn = 'multiChannelColumnsMode',
}

export interface TableCompareModes {
    [CompareMode.AbsDiffMode]: boolean;
    [CompareMode.RatioMode]: boolean;
    [CompareMode.ChannelOrVariantColumn]: boolean;
}

interface ActiveCompareModesPerKpi {
    [activeView: string]: {
        [dataKey: string]: TableCompareModes;
    };
}

export interface ImportingAbTest {
    channelName: string;
    testId: string;
}

interface MainStoreDependencies {
    updateVisibleColumnsLayout: () => void;
    updateMultigridLayout: () => void;
    getTableColumnListForAnalyticsTab: (analyticsTab: AnalyticsTab) => SelectorTableColumnDef[];
    getActiveAnalyticsTab: () => AnalyticsTab;
    getActiveTableFilters: () => TableFilter[];
    getTableColumns: () => SelectorTableColumnDefs;
    selectedChannelChanged: () => void;
    setTableSort: (
        sortedColumn: DataKey | 'DEFAULT',
        sortDirection: TableSortDirection | 'TOGGLE',
        compareMode?: CompareMode,
        compareChannel?: string
    ) => void;
    channelCompareIsAllowed: () => boolean;
    clearDiagramAndTable: () => void;
    setSelectedChannelWithoutReload: (channel: Channel | string) => void;
    getSelectedChannel: () => Channel | undefined;
    getMainDateRange: () => DateRange;
    setLastImportedDateForAbtest: (channelName: string, testId: string, lastImported?: moment.Moment) => void;
    getAbTestParameterViaUrl: () => {abTestId: string; variants: string[]};
    setAbTestParameterViaUrl: (abTestId: string, variants: string[]) => void;
}

export interface CompareState {
    compareData: CompareData;
    getCompareModeValueForKpi: (kpi: KPI) => TableCompareModes;
    allModesVisibleForKpi: (kpi: DataKey) => boolean;
    setCompareModeForKpi: (kpi: DataKey, compareMode: CompareMode, active: boolean) => void;
    getChannelColor: (channelName: string, channelNames: string[]) => ColorHelper;
    tableDataForChannel: (tableDataParams: TableDataForChannelRequest) => Promise<TableDataForChannelResponse[] | TableDataForChannel[]>;
    stickyRowSums: (stickyRowParams: StickyRowSumsRequest) => Promise<TableDataForChannelResponse[]>;
    getMultiChannelReplacementRows: (kpi: KPI) => SelectorTableColumnDef[];
    calculateRatioValues: (channelDataRegister: ChannelDataRegister, dataKey: DataKey) => RatioResultItem[];
    applyChannelCompareSettings: (channelNames: string[]) => void;
    checkIfColumnShouldBeAffectedByChannelComparison: (dataKey: DataKey) => boolean;
    ensureComparisionTargetCurrency: (availableChannels: Channel[]) => void;
    comparisonTargetCurrency: string;
    setAndApplyNewCompareTargetCurrency: (currency: string) => void;
    compareIsEnabled: boolean;
    applyAbtestCompareSettings: (channelName?: string, testId?: string, variantIds?: string[]) => void;
    importAbTest: (channelName: string, testId: string, lastImported?: moment.Moment) => void;
    importingAbTests: ImportingAbTest[];
}

class ChannelCompareStore implements HistoryHandler, CompareState {
    constructor(private readonly storeDependencies: MainStoreDependencies) {
        const abTestUrlParams = this.storeDependencies.getAbTestParameterViaUrl();

        if (abTestUrlParams.abTestId !== '' && abTestUrlParams.variants.length > 1) {
            this.compareData = {
                compareEnabled: true,
                variantChannelCompare: {
                    testId: abTestUrlParams.abTestId,
                    variantIds: abTestUrlParams.variants,
                },
            };
        }
    }

    @observable private activeCompareModesPerKpi: ActiveCompareModesPerKpi = this.generateDefaultCompareModeValues();
    @observable public comparisonTargetCurrency = '';
    @observable public importingAbTests: ImportingAbTest[] = [];

    @observable
    public compareData: CompareData = {
        compareEnabled: false,
    };

    @computed
    public get compareIsEnabled(): boolean {
        return this.compareData.compareEnabled && this.storeDependencies.channelCompareIsAllowed();
    }

    public importAbTest = (channelName: string, testId: string, lastImported?: moment.Moment): void => {
        const mainDateRange = this.storeDependencies.getMainDateRange();
        if (this.importingAbTests.some((importing) => importing.channelName === channelName && importing.testId === testId)) {
            return;
        }
        if (!mainDateRange.startDate || !mainDateRange.endDate || !lastImported) {
            return;
        }

        if (lastImported.isAfter(mainDateRange.endDate)) {
            return;
        }

        this.importingAbTests.push({
            channelName,
            testId,
        });

        const removeFromImportingTests = () =>
            (this.importingAbTests = this.importingAbTests.filter(
                (importing) => importing.testId !== testId && importing.channelName === channelName
            ));

        const importRange: DateRange = {
            startDate: lastImported,
            endDate: mainDateRange.endDate,
        };

        AbTestAPI.importAbTest(channelName, testId, DateUtils.toDailyDateRange(importRange))
            .then(() => {
                this.storeDependencies.setLastImportedDateForAbtest(channelName, testId, importRange.endDate);
                removeFromImportingTests();
            })
            .catch(() => removeFromImportingTests());
    };

    public getPartialHistory = (): Partial<HistoryItem> => {
        const {compareData} = this;
        if (this.compareIsEnabled && compareData) {
            return {
                compareData: {
                    channelCompare: compareData.channelCompare,
                    variantChannelCompare: compareData.variantChannelCompare,
                },
            };
        }
        return {
            compareData: undefined,
        };
    };

    public applyHistory = (history: HistoryItem): void => {
        const {compareData, channel} = history;
        if (compareData) {
            if (compareData.channelCompare) {
                const {channelNames} = compareData.channelCompare;
                this.applyChannelCompareSettings(channelNames, true);
            } else if (compareData.variantChannelCompare) {
                const {testId, variantIds} = compareData.variantChannelCompare;
                this.applyAbtestCompareSettings(channel, testId, variantIds, true);
            }
        } else if (this.compareIsEnabled) {
            this.applyChannelCompareSettings([], true);
        }
    };

    public setAndApplyNewCompareTargetCurrency = (currency: string) => {
        this.comparisonTargetCurrency = currency;
        this.storeDependencies.selectedChannelChanged();
    };

    public ensureComparisionTargetCurrency = (availableChannels: Channel[]) => {
        if (availableChannels.length === 0) {
            return;
        }
        if (!this.comparisonTargetCurrency) {
            this.comparisonTargetCurrency = availableChannels[0].currency;
            return;
        }
        const targetCurrencyIsAvailable = availableChannels
            .map((channel) => channel.currency)
            .some((currency) => currency === this.comparisonTargetCurrency);

        if (!targetCurrencyIsAvailable) {
            this.comparisonTargetCurrency = availableChannels[0].currency;
        }
    };

    public getCompareModeValueForKpi(kpi: KPI): TableCompareModes {
        const {getActiveAnalyticsTab} = this.storeDependencies;
        const dataForTab = this.activeCompareModesPerKpi[getActiveAnalyticsTab()];
        return dataForTab && dataForTab[kpi];
    }

    public allModesVisibleForKpi(kpi: DataKey): boolean {
        const {getActiveAnalyticsTab} = this.storeDependencies;
        const compareModeForCurrentTab = this.activeCompareModesPerKpi[getActiveAnalyticsTab()];
        if (compareModeForCurrentTab && compareModeForCurrentTab[kpi]) {
            const compareModeForCurrentKpi = compareModeForCurrentTab[kpi];
            return compareModeForCurrentKpi.multiChannelColumnsMode && compareModeForCurrentKpi.ratioMode && compareModeForCurrentKpi.absDiffMode;
        }
        return false;
    }

    public setCompareModeForKpi(kpi: DataKey, compareMode: CompareMode, active: boolean) {
        const {getActiveAnalyticsTab} = this.storeDependencies;
        const compareModeForCurrentTab = this.activeCompareModesPerKpi[getActiveAnalyticsTab()];
        if (compareModeForCurrentTab) {
            compareModeForCurrentTab[kpi][compareMode] = active;
        }
    }

    public getChannelColor(channel: string, channels: string[]): ColorHelper {
        const channelIndex = channels.findIndex((channelEntry) => channelEntry === channel);
        if (channelIndex !== -1 && DIAGRAM_COLORS[channelIndex] !== undefined) {
            return DIAGRAM_COLORS[channelIndex];
        }

        return color(BlackColor);
    }

    public tableDataForChannel(tableDataParams: TableDataForChannelRequest): Promise<TableDataForChannel[]> {
        if (this.compareIsEnabled) {
            return this.getChannelComparisonChannelData(tableDataParams);
        }

        return TableAPI.tableDataForChannel({
            ...tableDataParams,
            interval: DateUtils.toDailyDateRange(tableDataParams.interval),
        }).then((resp) => resp.data);
    }

    public stickyRowSums(stickyRowParams: StickyRowSumsRequest): Promise<TableDataForChannel[]> {
        if (this.compareIsEnabled) {
            return this.getChannelComparisonStickyRowSums(stickyRowParams);
        }

        return TableAPI.stickyRowSums({
            ...stickyRowParams,
            interval: DateUtils.toDailyDateRange(stickyRowParams.interval),
        }).then((resp) => resp.data);
    }

    public getMultiChannelReplacementRows(kpi: KPI): SelectorTableColumnDef[] {
        const compareModesForKPI = this.getCompareModeValueForKpi(kpi);
        const tableColumns = this.storeDependencies.getTableColumns();
        const {channelCompare, variantChannelCompare} = this.compareData;

        if (this.compareIsEnabled && compareModesForKPI.multiChannelColumnsMode) {
            const compareTargets = this.getCompareTarget(channelCompare, variantChannelCompare);
            return compareTargets.map((targetName) => ({
                ...tableColumns[kpi],
                compareColumnInfo: {compareMode: CompareMode.ChannelOrVariantColumn, targetName},
            }));
        }
        return [];
    }

    private getCompareTarget = (channelCompare?: {channelNames: string[]}, variantIdCompare?: VariantChannelCompare): string[] => {
        if (channelCompare) {
            return channelCompare.channelNames;
        } else if (variantIdCompare) {
            return variantIdCompare.variantIds;
        }
        return [];
    };

    public calculateRatioValues(channelDataRegister: ChannelDataRegister, dataKey: DataKey): RatioResultItem[] {
        const dataByKey = (data: TableDataForChannelResponse) => (data as any)[dataKey];
        const total = _.sumBy(Object.values(channelDataRegister), (o) => dataByKey(o));
        const channels = Object.keys(channelDataRegister);
        const {channelCompare, variantChannelCompare} = this.compareData;
        const compareTargets = this.getCompareTarget(channelCompare, variantChannelCompare);

        return compareTargets.map((target) => ({
            channel: target,
            color: this.getChannelColor(target, compareTargets),
            percentage: total > 0 ? dataByKey(channelDataRegister[target]) / total : 1 / channels.length,
        }));
    }

    public applyAbtestCompareSettings = (channelName?: string, testId?: string, variantIds?: string[], applyHistory = false) => {
        if (channelName && testId && variantIds && variantIds.length > 1) {
            this.compareData = {
                compareEnabled: true,
                variantChannelCompare: {
                    testId,
                    variantIds,
                },
            };
            this.storeDependencies.setSelectedChannelWithoutReload(channelName);
        } else {
            this.compareData = {
                compareEnabled: false,
            };
        }
        this.refreshAfterCompareApply(applyHistory);
    };

    private refreshAfterCompareApply(applyHistory = false) {
        if (this.compareIsEnabled) {
            this.storeDependencies.setTableSort('DEFAULT', TableSortDirection.DESC, CompareMode.AbsDiffMode);
        } else {
            this.storeDependencies.setTableSort('DEFAULT', TableSortDirection.DESC);
        }

        if (this.compareData.variantChannelCompare) {
            this.storeDependencies.setAbTestParameterViaUrl(
                this.compareData.variantChannelCompare.testId,
                this.compareData.variantChannelCompare.variantIds
            );
        } else {
            this.storeDependencies.setAbTestParameterViaUrl('', []);
        }

        this.storeDependencies.clearDiagramAndTable();
        const {selectedChannelChanged, updateMultigridLayout, updateVisibleColumnsLayout} = this.storeDependencies;
        updateVisibleColumnsLayout();
        updateMultigridLayout();
        if (!applyHistory) {
            this.storeDependencies.clearDiagramAndTable();
            selectedChannelChanged();
        }
    }

    public applyChannelCompareSettings(channelNames: string[], applyHistory = false) {
        if (channelNames.length !== 0) {
            this.compareData = {
                compareEnabled: true,
                channelCompare: {
                    channelNames,
                },
            };
        } else {
            this.compareData = {
                compareEnabled: false,
            };
        }
        this.refreshAfterCompareApply(applyHistory);
    }

    public checkIfColumnShouldBeAffectedByChannelComparison(dataKey: DataKey): boolean {
        return !(
            dataKey === KPIGroupKey.Searchterm ||
            dataKey === NoneKPIColumns.RowNumber ||
            dataKey === KPIGroupKey.ProductId ||
            dataKey === DescriptiveKPIAdditions.Title
        );
    }

    private generateDefaultCompareModeValues(): ActiveCompareModesPerKpi {
        const {getTableColumnListForAnalyticsTab} = this.storeDependencies;
        const getDefaultCompareModes = (tableColummList: SelectorTableColumnDef[]) =>
            tableColummList.reduce((activeCompareModes, columnDef) => {
                activeCompareModes[columnDef.dataKey] = {
                    [CompareMode.AbsDiffMode]: true,
                    [CompareMode.RatioMode]: false,
                    [CompareMode.ChannelOrVariantColumn]: false,
                };
                return activeCompareModes;
            }, {} as {[key: string]: TableCompareModes});

        const searchCompareModes = getDefaultCompareModes(getTableColumnListForAnalyticsTab(AnalyticsTab.SearchView));
        const productCompareModes = getDefaultCompareModes(getTableColumnListForAnalyticsTab(AnalyticsTab.ProductView));
        return {
            [AnalyticsTab.SearchView]: searchCompareModes,
            [AnalyticsTab.ProductView]: productCompareModes,
        };
    }

    private changeKpiIfInChannelCompareContext(tableDataParams: TableDataForChannelRequest): AbtestSortAttribute | string {
        const {compareMode, targetName} = tableDataParams.sorting;
        if (isKpiGroupKeyOrDescriptiveKpiAdditions(tableDataParams.sorting.attribute)) {
            return tableDataParams.sorting.attribute;
        }
        const {channelCompare} = this.compareData;
        const compareChannels = channelCompare ? channelCompare.channelNames : [];
        switch (compareMode) {
            case CompareMode.RatioMode:
                if (compareChannels.length === 2) {
                    return {
                        mode: SortMode.Ratio,
                        channels: compareChannels,
                        kpi: tableDataParams.sorting.attribute,
                    };
                }
                throw Error('Sorting for more than 2 channels as diff column currently not supported');
            case CompareMode.ChannelOrVariantColumn:
                return {
                    mode: SortMode.Variant,
                    kpi: tableDataParams.sorting.attribute,
                    channel: targetName,
                };
            default:
                return {
                    mode: SortMode.Diff,
                    kpi: tableDataParams.sorting.attribute,
                };
        }
    }

    private changeKpiIfInVariantCompareContext(
        tableDataParams: TableDataForChannelRequest,
        variantChannelCompare: VariantChannelCompare
    ): AbtestSortAttribute {
        const {compareMode, targetName} = tableDataParams.sorting;
        const compareVariants = variantChannelCompare ? variantChannelCompare.variantIds : [];
        switch (compareMode) {
            case CompareMode.RatioMode:
                if (compareVariants.length === 2) {
                    return {
                        channel: tableDataParams.channel,
                        kpi: tableDataParams.sorting.attribute,
                        mode: SortMode.Ratio,
                        testId: variantChannelCompare.testId,
                        variantIds: variantChannelCompare.variantIds,
                    };
                }
                throw Error('Sorting for more than 2 channels as diff column currently not supported');
            case CompareMode.ChannelOrVariantColumn:
                if (targetName) {
                    return {
                        mode: SortMode.Variant,
                        kpi: tableDataParams.sorting.attribute,
                        channel: tableDataParams.channel,
                        testId: variantChannelCompare.testId,
                        variantIds: [targetName],
                    };
                }
                throw Error('Variant column needs one variant [targetName]');
            default:
                return {
                    mode: SortMode.Diff,
                    kpi: tableDataParams.sorting.attribute,
                };
        }
    }

    private addPrefixToFilterKpi = (filter: {attribute: KPI; compareColumnInfo: CompareColumnInfo; rules: FilterRule}) => {
        const {channelCompare, variantChannelCompare} = this.compareData;
        switch (filter.compareColumnInfo.compareMode) {
            case CompareMode.AbsDiffMode:
                if (channelCompare || variantChannelCompare) {
                    return {
                        mode: SortMode.Diff,
                        kpi: filter.attribute,
                    };
                }
                return filter.attribute;
            case CompareMode.ChannelOrVariantColumn:
                if (channelCompare) {
                    return {
                        mode: SortMode.Variant,
                        kpi: filter.attribute,
                        channel: filter.compareColumnInfo.targetName ? filter.compareColumnInfo.targetName : '',
                    };
                } else if (variantChannelCompare) {
                    const selectedChannel = this.storeDependencies.getSelectedChannel();
                    return {
                        mode: SortMode.Variant,
                        kpi: filter.attribute,
                        variantIds:
                            filter.compareColumnInfo && filter.compareColumnInfo.targetName ? [filter.compareColumnInfo.targetName] : undefined,
                        channel: selectedChannel ? selectedChannel.name : undefined,
                        testId: variantChannelCompare.testId,
                    };
                }
                return filter.attribute;
            default:
                return filter.attribute;
        }
    };

    private generateChannelCompareTableFilters(): string {
        const activeTableFilters = this.storeDependencies.getActiveTableFilters();
        return JSON.stringify(
            activeTableFilters
                .filter((filter) => filter.compareColumnInfo)
                .map((filter: {attribute: KPI; compareColumnInfo: CompareColumnInfo; rules: FilterRule}) => {
                    return {
                        attribute: this.addPrefixToFilterKpi(filter),
                        comparator: filter.rules.operator,
                        value: filter.rules.value,
                    };
                })
        );
    }

    private getChannelComparisonChannelData(tableDataParams: TableDataForChannelRequest): Promise<TableDataForChannel[]> {
        const {channelCompare, variantChannelCompare} = this.compareData;
        const baseRequest = {
            limit: tableDataParams.limit,
            filter: this.generateChannelCompareTableFilters(),
            interval: DateUtils.toDailyDateRange(tableDataParams.interval),
            search: tableDataParams.search,
            mode: tableDataParams.mode,
            targetCurrency: this.comparisonTargetCurrency,
        };
        if (variantChannelCompare) {
            return AbTestAPI.tableDataForAbtestComparison({
                ...baseRequest,
                channel: tableDataParams.channel,
                testId: variantChannelCompare.testId,
                variantIds: toJS(variantChannelCompare.variantIds),
                sortDirection: tableDataParams.sorting.sortDirection,
                sortAttribute: JSON.stringify(this.changeKpiIfInVariantCompareContext(tableDataParams, variantChannelCompare)),
            })
                .then((data) => this.renameKeysToOnlyVariant(data, tableDataParams.channel, variantChannelCompare.testId))
                .then(this.formatToTableDataForChannel);
        } else if (channelCompare) {
            return TableAPI.tableDataForChannelComparison({
                ...baseRequest,
                sortAttribute: JSON.stringify(this.changeKpiIfInChannelCompareContext(tableDataParams)),
                sortDirection: tableDataParams.sorting.sortDirection,
                channels: toJS(channelCompare ? channelCompare.channelNames : []),
            }).then(this.formatToTableDataForChannel);
        }

        return Promise.resolve([]);
    }

    private createDummyTableDataEntry(channelData: {[channel: string]: TableDataForChannelResponse}): TableDataForChannelResponse {
        const dummyKpiBaseValues: any = Object.values(channelData)[0];
        return Object.keys(dummyKpiBaseValues).reduce((dummyEntry: any, kpi) => {
            if (isKpiGroupKeyOrDescriptiveKpiAdditions(kpi)) {
                dummyEntry[kpi] = dummyKpiBaseValues[kpi];
            } else {
                dummyEntry[kpi] = 0;
            }
            return dummyEntry;
        }, {} as TableDataForChannelResponse);
    }

    private renameKeysToOnlyVariant = (data: ChannelDataRegister[], channelName: string, testId: string): ChannelDataRegister[] => {
        return data.map((channelRegister) => {
            const keys = Object.keys(channelRegister);
            return keys.reduce<Record<string, TableDataForChannelResponse>>((newRegister, key) => {
                const newKey = key.replace(`${channelName}_${testId}_`, '');
                newRegister[newKey] = channelRegister[key];
                return newRegister;
            }, {});
        });
    };

    private formatToTableDataForChannel = (data: ChannelDataRegister[]): TableDataForChannel[] => {
        return data.map((tableData) => ({
            ...this.createDummyTableDataEntry(tableData),
            channelCompareRegister: tableData,
        }));
    };

    private getChannelComparisonStickyRowSums(stickyRowSumsParams: StickyRowSumsRequest): Promise<TableDataForChannelResponse[]> {
        const {channelCompare, variantChannelCompare} = this.compareData;
        if (variantChannelCompare) {
            return AbTestAPI.stickyRowSumsForAbtestComparison({
                interval: DateUtils.toDailyDateRange(stickyRowSumsParams.interval),
                search: stickyRowSumsParams.search,
                mode: stickyRowSumsParams.mode,
                channel: stickyRowSumsParams.channel,
                targetCurrency: this.comparisonTargetCurrency,
                testId: variantChannelCompare.testId,
                variantIds: toJS(variantChannelCompare.variantIds),
            })
                .then((data) => this.renameKeysToOnlyVariant(data, stickyRowSumsParams.channel, variantChannelCompare.testId))
                .then(this.formatToTableDataForChannel);
        } else if (channelCompare) {
            return TableAPI.stickyRowSumsForChannelComparison({
                interval: DateUtils.toDailyDateRange(stickyRowSumsParams.interval),
                search: stickyRowSumsParams.search,
                mode: stickyRowSumsParams.mode,
                channels: toJS(channelCompare ? channelCompare.channelNames : []),
                targetCurrency: this.comparisonTargetCurrency,
            }).then(this.formatToTableDataForChannel);
        }
        return Promise.resolve([]);
    }
}

export default ChannelCompareStore;



// WEBPACK FOOTER //
// ./src/stores/CompareStore.ts